export const en = {
  common: {
    generating: 'Generating...',
    analyzing: 'Analyzing...',
    copy: 'Copy',
    copied: 'Copied!',
    copyAll: 'Copy All',
    close: 'Close',
    retry: 'Retry',
    clear: 'Clear',
    cancel: 'Cancel',
    save: 'Save',
    saved: 'Saved',
    delete: 'Delete',
    reset: 'Reset',
    loading: 'Loading...',
    score: 'Score',
    add: 'Add',
    clickToCopy: 'Click to copy',
    savedToIdeas: 'Saved to ideas!',
    savedToDrafts: 'Saved to drafts!',
    chars: 'chars',
    deleteConfirm: 'Are you sure you want to delete this?',
    deleteAllConfirm: 'Delete all? This cannot be undone.',
    noExportData: 'No data to export.',
    done: 'Done',
    edit: 'Edit',
    ok: 'OK',
    confirm: 'Confirm',
    or: 'or',
    postToX: 'Post to X',
    postedToX: 'Posted to X successfully',
    processing: 'Processing...',
    show: 'Show',
    hide: 'Hide',
    error: 'Something went wrong. Please try again.',
  },

  sidebar: {
    title: 'Bangerfy',
    subtitle: 'Your AI ghostwriter for X',
    sections: {
      create: 'CREATE',
      analyze: 'ANALYZE',
      grow: 'GROW',
    },
    nav: {
      brainDump: 'Brain Dump',
      brainDumpSub: 'Dump your thoughts',
      composeIdeas: 'Compose Ideas',
      composeIdeasSub: 'Build from blocks',
      contentCoach: 'Content Coach',
      contentCoachSub: 'Get feedback',
      algoAnalyzer: 'Algo Analyzer',
      algoAnalyzerSub: 'Score your post',
      replyGuy: 'Reply Guy',
      replyGuySub: 'Craft replies',
      inspiration: 'Inspiration',
      inspirationSub: 'Find ideas',
      accountResearcher: 'Account Researcher',
      accountResearcherSub: 'Learn patterns',
      history: 'History',
      historySub: 'Past results',
      education: 'Education',
      educationSub: 'Learn the game',
      drafts: 'Drafts',
      draftsSub: 'Saved drafts',
      analytics: 'Analytics',
      analyticsSub: 'Your stats',
      settings: 'Settings',
      settingsSub: 'Configure',
      admin: 'Admin',
      adminSub: 'System',
      more: 'More',
    },
    footer: '© 2026 Bangerfy',
  },

  login: {
    badge: 'AI-powered tweeting for creators',
    heroLine1: 'Write tweets that hit.',
    heroLine2: 'Every single time.',
    heroSub: '9 AI tools that learn your voice, score your content, and help you post with confidence. Your ghostwriter for X.',
    getStarted: 'Get Started Free',
    skipToSetup: 'See Demo',
    social: {
      creators: 'Creators',
      tweets: 'Tweets generated',
      hours: 'Hours saved',
    },
    tools: {
      brain: { label: 'Brain Dump', desc: 'Dump raw thoughts, get tweet-ready content. AI turns your stream of consciousness into viral posts.' },
      compose: { label: 'Compose Ideas', desc: 'Build tweets from idea blocks. Combine concepts and generate scored variations.' },
      coach: { label: 'Content Coach', desc: 'Get real-time feedback on hook strength, clarity, engagement, and CTA effectiveness.' },
      reply: { label: 'Reply Guy', desc: 'Craft perfect replies in any tone — empathetic, witty, expert, or casual.' },
      analytics: { label: 'Analytics', desc: 'Deep-dive into your posting patterns. Find your best times, topics, and engagement triggers.' },
      algo: { label: 'Algo Analyzer', desc: 'Score any post against 8 algorithm-backed metrics before you hit publish.' },
      researcher: { label: 'Account Researcher', desc: 'Analyze any account\'s success patterns — tone, topics, engagement triggers.' },
      inspiration: { label: 'Inspiration', desc: 'Find trending topics and generate fresh tweet ideas from any keyword.' },
      education: { label: 'Education', desc: 'Learn the X algorithm, content strategy, and growth tactics with guided lessons.' },
    },
    features: {
      sectionLabel: 'Tools',
      sectionTitle: '9 tools. One workflow.',
      sectionDesc: 'Everything you need to create, analyze, and grow on X — powered by AI that learns your style.',
      voiceTitle: 'Style Clone',
      voiceDesc: 'Upload your tweets. AI learns your tone, quirks, and patterns. Every post sounds like you.',
      styleTitle: 'Viral Scoring',
      styleDesc: 'Every tweet scored against 8 algorithm-backed metrics before you hit post.',
      viralTitle: 'Full Toolkit',
      viralDesc: 'Brain dump, reply crafting, account research, content coaching — all in one place.',
    },
    howItWorks: 'How it works',
    howTitle: 'From thought to tweet in 60 seconds',
    howStep1Title: 'Upload',
    howStep2Title: 'Generate',
    howStep3Title: 'Post',
    step1: 'Upload your past tweets or start from scratch. AI learns your unique voice and patterns.',
    step2: 'AI generates multiple variations in your style, each scored on 8 viral metrics.',
    step3: 'Pick the best, tweak if needed, and post with confidence.',
    pricing: {
      sectionLabel: 'Pricing',
      sectionTitle: 'Start free. Scale when ready.',
      sectionDesc: 'All tools included in the free plan. Bring your own API key and start posting.',
      free: {
        name: 'Free',
        price: '$0',
        period: '/forever',
        desc: 'Bring your own API key',
        f1: 'All 9 AI tools',
        f2: 'Unlimited generations',
        f3: 'Style & voice cloning',
        f4: 'Tweet scoring & analytics',
        f5: 'Use your own API key',
        cta: 'Get Started',
      },
    },
    mock: {
      input: 'I just realized that the best tweets come from shower thoughts. Not planned content, not threads — just one raw observation that hits different...',
      tweet1: 'Your best tweets are already in your head. They\'re the things you mutter to yourself at 2am.',
      tweet2: 'Stop planning content. Start capturing thoughts. The algorithm rewards authenticity, not strategy.',
    },
    ctaTitle: 'Stop overthinking. Start posting.',
    ctaDesc: 'Join creators who save hours every week with AI-powered tweeting.',
    ctaButton: 'Get Started Free',
    ctaHint: 'No credit card required. Bring your own API key.',
    footer: '\u00A9 2026 Bangerfy. All rights reserved.',
  },

  loginPage: {
    appDescription: 'Generate and optimize tweets with AI',
    signInWithGoogle: 'Sign in with Google',
    email: 'Email',
    password: 'Password (6+ characters)',
    loginButton: 'Log In',
    signupButton: 'Create Account',
    enterEmailAndPassword: 'Please enter your email and password',
    passwordTooShort: 'Password must be 6 or more characters',
    confirmationSent: 'Confirmation email sent',
    confirmationDesc: 'We sent a confirmation email to {{email}}. Click the link to activate your account.',
    backToLogin: 'Back to login',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    signUp: 'Sign up',
    logIn: 'Log in',
  },

  authCallback: {
    authenticating: 'Authenticating...',
    pleaseWait: 'Please wait',
    authSuccess: 'Authentication successful',
    navigatingHome: 'Navigating to home...',
    authFailed: 'Authentication failed',
    authCodeNotFound: 'Authentication code not found',
  },

  brainDump: {
    title: 'Brain Dump',
    subtitle: 'Dump your raw thoughts and turn them into tweet-ready content',
    countLabel: 'Count:',
    recommended: '(recommended)',
    showAllLabel: 'Show all (unchecked: diverse Top 5)',
    hideSettings: 'Hide settings',
    advanced: 'Advanced',
    placeholder: 'Write whatever is on your mind...\n\nExamples:\n- Something I learned today\n- A topic on my mind\n- A message I want to share\n- A random idea',
    generateTweets: 'Generate Tweets',
    strategyButton: 'Strategy (Standard + Teaser + Note promo + Thread)',
    strategyDesc: '7-step reasoning \u2192 Standard post + Teaser/Answer pair + Note promo 3 patterns generated at once',
    generatingCandidates: 'Generating candidates...',
    strategyGenerating: 'Strategy generating...',
    progressDesc: 'Template generation \u2192 Scoring \u2192 Diversity check in progress',
    strategyProgressDesc: 'Expanding 7-step reasoning into standard, teaser, note promo & thread',
    errorHints: {
      api: 'Check API Key / model settings',
      input: 'Try shorter input text',
      wait: 'Wait a few seconds and retry',
    },
    results: 'Generated Tweets ({{count}} total)',
    edit: 'Edit',
    saveToIdeas: 'Save to ideas',
    hideScoreDetails: 'Hide score details',
    showScoreDetails: 'Show score details',
    freshness: 'Freshness',
    specificity: 'Specificity',
    personaFit: 'Persona fit',
    replyHook: 'Reply hook',
    strategySet: 'Strategy Set',
    strategyLabel: 'Teaser\u2192Answer / Note promo / Thread',
    standardTweet: 'Standard Tweet',
    shortThread: 'Short Thread (3 posts)',
    teaserAnswer: 'Teaser \u2192 Answer',
    teaserLabel: 'Post 1 / Teaser',
    answerLabel: 'Post 2 / Answer',
    notePromo: 'Note Promo 3 Patterns',
    showThoughtLog: 'Show thought log',
    hideThoughtLog: 'Hide thought log',
    voiceInput: 'Voice input',
    stopRecording: 'Stop recording',
    transcribing: 'Transcribing...',
    voiceAdded: 'Voice input added',
  },

  tweetSettings: {
    title: 'Tweet Strategy',
    description: 'Set length, tone, style to stabilize generation results',
    reset: 'Reset',
    lengthLabel: 'Length',
    lengthHint: 'Optimized within 280 chars',
    toneLabel: 'Tone',
    styleLabel: 'Style',
    humanizeLabel: 'AI-feel Removal Level',
    humanizeDesc: 'Lv1: Standard / Lv3: Slightly casual / Lv5: Human-first (verbose & natural OK)',
    length: {
      short: 'Short',
      shortHelper: '~60 chars \u00B7 Quick conclusion',
      medium: 'Medium',
      mediumHelper: '100-140 chars \u00B7 Quick read',
      long: 'Long',
      longHelper: '200+ chars \u00B7 Deep dive',
    },
    tone: {
      oneLiner: 'One-liner',
      oneLinerHelper: 'Sharp single phrase',
      explanatory: 'Explanatory',
      explanatoryHelper: 'Reason + background included',
      story: 'Story',
      storyHelper: 'Narrative arc',
    },
    style: {
      assertive: 'Assertive',
      assertiveHelper: 'Bold statements',
      empathetic: 'Empathetic',
      empatheticHelper: 'Warm & relatable',
      warning: 'Warning',
      warningHelper: 'Risk-focused alert',
      secret: 'Insider',
      secretHelper: 'Off-the-record vibe',
    },
  },

  quickActions: {
    toggle: 'Quick Templates',
    learning: 'Today I Learned',
    learningTemplate: 'What I learned today:\n\n',
    learningDesc: 'Share a learning',
    tips: 'Life Hack',
    tipsTemplate: 'Found a useful trick!\n\n',
    tipsDesc: 'Share a tip',
    opinion: 'Opinion',
    opinionTemplate: 'Something I\'ve been thinking about:\n\n',
    opinionDesc: 'Share your thoughts',
    question: 'Question',
    questionTemplate: 'I want to ask everyone:\n\n',
    questionDesc: 'Ask for opinions',
    experience: 'Experience',
    experienceTemplate: 'I recently had this experience:\n\n',
    experienceDesc: 'Share a story',
    thread: 'Thread',
    threadTemplate: 'Let me break this down in a thread \uD83D\uDC47\n\n1. ',
    threadDesc: 'Start a thread',
  },

  composeIdeas: {
    title: 'Compose Ideas',
    subtitle: 'Build tweets from idea blocks and generate variations',
    ideaBlocks: 'Idea Blocks',
    addBlock: '+ Add Block',
    blockLabel: 'BLOCK {{n}}',
    blockPlaceholder: 'Idea {{n}}...',
    combineBlocks: 'Combine Blocks \u2192',
    combinedTweet: 'Combined Tweet',
    combinedPlaceholder: 'Combined result will appear here...',
    generateVariations: 'Generate Variations',
    variations: 'Variations',
    variationLabel: 'Variation #{{n}} \u2014 Score: {{score}}',
  },

  contentCoach: {
    title: 'Content Coach',
    subtitle: 'Get AI-powered feedback to sharpen your tweets before posting',
    inputLabel: 'Your Draft Tweet',
    placeholder: 'Paste your draft tweet here for coaching...',
    coachingAreas: 'Coaching Areas',
    areas: {
      hook: 'Hook Strength',
      clarity: 'Clarity',
      engagement: 'Engagement',
      cta: 'Call to Action',
      length: 'Length Check',
    },
    getCoaching: 'Get Coaching',
    results: 'Coaching Results',
    overall: 'Overall',
    suggestion: 'Suggestion',
    improved: 'Improved Version',
    emptyState: 'Enter a tweet draft above,\nselect coaching areas, and get feedback',
  },

  algoAnalyzer: {
    title: 'Algo Analyzer',
    subtitle: 'Score your post against 8 algorithm-backed metrics',
    inputLabel: 'Content to Analyze',
    placeholder: 'Paste your tweet here...\n\nExample:\nHere\'s what I realized today \u2014 in programming, the courage to NOT aim for perfection is the most important thing. If your code works, that\'s enough. Refactor later.',
    analyzePost: 'Analyze Post',
    results: 'Analysis Results',
    overallScore: 'Overall Algo Score',
    excellent: 'Excellent! This post is highly optimized for the algorithm',
    good: 'Good content. A few tweaks could make it perform even better',
    needsWork: 'Room for improvement. Check the lower-scoring areas above',
    noResults: 'Could not retrieve analysis results',
    error: 'Error',
    errorMessage: 'Analysis failed. Please check your API key.',
    metrics: {
      hookStrength: 'Hook Strength',
      hookComment: 'Strong opening that stops the scroll',
      clarity: 'Clarity',
      clarityComment: 'Message is clear with no room for misunderstanding',
      engagement: 'Engagement Potential',
      engagementComment: 'Contains elements likely to earn likes and RTs',
      curiosity: 'Curiosity Gap',
      curiosityComment: 'Creates some desire to read more',
      emotional: 'Emotional Impact',
      emotionalComment: 'Effective emotional language',
      shareability: 'Shareability',
      shareabilityComment: 'Worth sharing with others',
      trendAlignment: 'Trend Alignment',
      trendComment: 'Moderate relevance to current trends',
      readability: 'Readability',
      readabilityComment: 'Easy to read structure and language',
    },
    systemPrompt: {
      instruction: 'Analyze the following tweet/post across 8 metrics and respond in JSON only.',
      contentLabel: 'Post content:',
      metricsLabel: 'Metrics (0-10 each):',
      hookStrength: 'Hook strength (does the first line grab attention)',
      clarity: 'Clarity (is the message easy to understand)',
      engagement: 'Engagement potential (likely to earn likes & RTs)',
      curiosity: 'Curiosity gap (makes you want to read more)',
      emotional: 'Emotional appeal (empathy, surprise, humor)',
      shareability: 'Shareability',
      trendAlignment: 'Trend alignment',
      readability: 'Readability (line breaks, length, rhythm)',
      formatLabel: 'Output format:',
      systemMessage: 'You are an expert X (Twitter) post analyst. Respond only in the specified JSON format.',
    },
  },

  replyGuy: {
    title: 'Reply Guy',
    subtitle: 'Craft engaging replies to join any conversation',
    originalTweet: 'Original Tweet',
    placeholder: 'Paste the tweet you want to reply to...',
    replyTone: 'Reply Tone',
    tones: {
      empathy: 'Empathy',
      empathyDesc: 'Show understanding and connection',
      question: 'Question',
      questionDesc: 'Expand the conversation',
      humor: 'Humor',
      humorDesc: 'Add a witty touch',
      expert: 'Expert',
      expertDesc: 'Share your expertise',
      casual: 'Casual',
      casualDesc: 'Keep it friendly and relaxed',
    },
    generateReplies: 'Generate Replies',
    topReplies: 'Top 5 Replies',
    emptyState: 'Enter a tweet on the left,\npick a tone, and generate replies',
  },

  inspiration: {
    title: 'Inspiration',
    subtitle: 'Find tweet ideas and trending topics to post about',
    trendingTopics: 'Trending Topics',
    getTrends: 'Get Trends',
    generateFromKeyword: 'Generate Ideas from Keyword',
    keywordPlaceholder: 'Enter a keyword... (e.g. React, AI, side hustle)',
    generate: 'Generate',
    ideasFor: 'Ideas for "{{keyword}}"',
    savedInspirations: 'Saved Inspirations',
    postsLabel: '{{count}} posts',
    more: '...+{{count}} more',
  },

  accountResearcher: {
    title: 'Account Researcher',
    subtitle: 'Analyze any account\'s posts to learn their success patterns',
    postsToAnalyze: 'Posts to Analyze',
    accountName: 'Account name (optional)',
    placeholder: 'Paste posts here, one per line...\n\nExample:\nThe most important thing in coding is not aiming for perfection\nI just realized: reading error messages solves most problems\nMorning coding sessions are 3x more productive than evening ones',
    postsEntered: '{{count}} posts entered',
    runAnalysis: 'Run Analysis',
    commonPatterns: 'Common Patterns',
    toneAnalysis: 'Tone Analysis',
    topicBreakdown: 'Topic Breakdown',
    engagementTriggers: 'Engagement Triggers',
    emptyState: 'Enter posts on the left and\nrun analysis to find patterns',
    pastAnalyses: 'Past Analyses',
    unnamed: 'Unnamed',
  },

  setupFlow: {
    title: 'Initial Setup',
    subtitle: 'Building your personal AI tweet generation engine',
    fetching: {
      title: 'Fetching past tweets...',
      desc: 'Loading your most recent 500 tweets',
    },
    analyzingStyle: {
      title: 'Analyzing your writing style...',
      desc: 'Learning your style from {{count}} tweets',
    },
    analyzingBuzz: {
      title: 'Analyzing buzz patterns...',
      desc: 'Extracting success factors from high-engagement tweets',
    },
    complete: {
      title: 'Setup Complete!',
      tweetsAnalyzed: '{{count}} tweets analyzed',
      styleModel: 'Writing style model created',
      buzzPatterns: 'Buzz patterns extracted',
      startButton: 'Get Started \u2192',
      buzzTitle: 'Your Buzz Patterns',
    },
    error: {
      title: 'An error occurred',
      commonCauses: 'Common causes:',
      invalidKey: 'Invalid OpenAI API Key',
      networkIssue: 'Network connection issue',
      tooFewTweets: 'Not enough tweets (minimum 10 required)',
      retryButton: 'Retry',
    },
    authError: 'Authentication info not found',
  },

  history: {
    title: 'History',
    subtitle: 'Search and organize your past generations and analyses',
    exportCSV: 'Export CSV',
    deleteAll: 'Delete All',
    filters: {
      all: 'All',
      tweet: 'Tweet',
      analysis: 'Analysis',
      coaching: 'Coaching',
      reply: 'Reply',
      inspiration: 'Inspiration',
      research: 'Research',
    },
    searchPlaceholder: 'Search by content or input text',
    dateRange: {
      all: 'All time',
      day: 'Last 24 hours',
      week: 'Last 7 days',
      month: 'Last 30 days',
    },
    sort: {
      newest: 'Newest first',
      oldest: 'Oldest first',
    },
    showing: '{{filtered}} shown ({{total}} total)',
    emptyState: 'No matching history found.',
    emptyHint: 'Try loosening your filters.',
    emptyDefault: 'Generate or analyze content to see history here.',
  },

  rightSidebar: {
    savedIdeas: 'Saved Ideas ({{count}})',
    history: 'History ({{count}})',
    folders: 'FOLDERS',
    newFolder: '+ New Folder',
    folderPlaceholder: 'Folder name',
    allIdeas: 'All Ideas',
    emptyIdeas: 'No saved ideas yet.\nBookmark results from any feature to save them here.',
    emptyHistory: 'No history yet.',
  },

  education: {
    title: 'Education',
    subtitle: 'Learn effective usage and study materials',
    progress: 'Learning Progress',
    categories: 'Categories',
    completed: '{{done}}/{{total}} completed',
    codeExample: 'Code Example',
    markAsRead: 'Mark as Read',
  },

  admin: {
    title: 'Settings',
    subtitle: 'Manage API keys and configurations',
    authMode: 'Authentication Mode',
    byok: 'Your Own API Key',
    byokDesc: 'Enter API keys from each provider directly. Pay-as-you-go billing.',
    subscription: 'Subscription',
    subscriptionPreparing: 'Coming soon',
    subscriptionDesc: 'Monthly plan, no keys needed. Flat-rate access to all providers.',
    subscriptionComingSoon: 'Coming soon. Please use BYOK (Your Own API Key) mode for now.',
    subscriptionTitle: 'Subscription',
    subscriptionFullDesc: 'Access all AI features with a monthly plan, no keys needed.',
    activeProvider: 'Active AI Provider',
    configured: 'Configured',
    notConfigured: 'Not configured',
    providerNote: 'You can set separate API keys per provider. Text generation uses the selected provider.',
    howItWorks: 'How it works',
    howStep1: 'API keys are stored only in your browser, never on our servers',
    howStep2: 'Keys are sent to our server on request and forwarded to each provider\'s API',
    howStep3: 'Our server only forwards keys — no logging or storage',
    inUse: 'Active',
    textGeneration: 'Text generation',
    speechRecognition: 'Speech recognition',
    loggedIn: 'Logged in',
    apiKey: 'API Key',
    googleAccount: 'Google Account',
    googleLoginFailed: 'Login failed',
    enterApiKey: 'Please enter an API key',
    invalidKeyFormat: 'Invalid format. {{name}} API keys start with "{{prefix}}".',
    logout: 'Log out',
    loggingIn: 'Logging in...',
    loginWithGoogle: 'Sign in with Google Account',
    googleAiPremiumDesc: 'With a Google One AI Premium (Gemini Advanced) subscription, you can use Gemini without an API key.',
    googleClientIdMissing: 'Google Client ID not configured',
    googleClientIdDesc: 'To use Google login, create an OAuth Client ID in Google Cloud Console and set REACT_APP_GOOGLE_CLIENT_ID.',
    storagePersistent: 'Local Storage (persistent)',
    storageSession: 'Session only (cleared when tab closes)',
    storageLabel: 'Storage:',
    sessionOnly: 'Session only',
    persistent: 'Persistent',
    getKeyFrom: 'Get your API key from',
    model: 'Model',
  },

  settings: {
    title: 'AI Settings',
    noApiKeysNeeded: 'API keys are not handled in the frontend.',
    backendExplanation1: 'AI inference runs through a secure backend (/api/generate, /api/transcribe).',
    backendExplanation2: 'Authentication uses REACT_APP_AUTH_TOKEN, embedded at build time.',
    backendExplanation3: 'No need to store API keys in the browser.',
    envVarTitle: 'Environment Variable Setup',
    envVarDesc: 'Set REACT_APP_AUTH_TOKEN in your project\'s .env or Vercel environment variables.',
  },

  analytics: {
    subtitle: 'Analyze your posting history for insights',
    importSuccess: '{{count}} tweets imported',
    importError: 'Import failed: {{error}}',
    confirmSample: 'Generate sample data (50 tweets)?',
    confirmDelete: 'Delete all data?',
    empty: {
      subtitle: 'Analyze your posting history to find your optimal strategy',
      message: 'No data available. Add data using one of the methods below:',
      importCsv: 'Import CSV file',
      generateSample: 'Generate sample data (demo)',
    },
    csvFormat: 'CSV format example:',
    manage: 'Data Management',
    menu: {
      import: 'CSV Import',
      sample: 'Generate Sample',
      deleteAll: 'Delete All',
    },
    tabs: {
      overview: 'Overview',
      topics: 'Topics',
      timing: 'Timing',
      trends: 'Trends',
    },
    insights: 'Insights',
    metric: {
      totalTweets: 'Total Tweets',
      avgLikes: 'Avg Likes',
      avgRt: 'Avg RT',
      avgReplies: 'Avg Replies',
    },
    best: 'Best Tweet',
    worst: 'Room for Improvement',
    labels: {
      likes: 'Likes',
      rt: 'RT',
      replies: 'Replies',
      tweet: 'tweet',
    },
    topics: {
      distribution: 'Topic Distribution',
      engagement: 'Topic Engagement',
    },
    timing: {
      heatmap: 'Posting Time Heatmap',
      heatmapDesc: 'Darker colors indicate higher engagement time slots',
      time: 'Time',
      hour: 'h',
      bestTitle: 'Top 5 Best Posting Times',
      dayOfWeek: 'Day',
      postCount: 'posts',
      avgEngagement: 'Avg Engagement',
    },
    'days.0': 'Sun', 'days.1': 'Mon', 'days.2': 'Tue', 'days.3': 'Wed', 'days.4': 'Thu', 'days.5': 'Fri', 'days.6': 'Sat',
    trends: {
      title: 'Last 30 Days Trend',
      tweets: 'Tweets',
      likes: 'Likes',
      retweets: 'Retweets',
      replies: 'Replies',
    },
  },

  home: {
    subtitle: 'Generate tweet ideas instantly from text or voice',
    settingsButton: 'Settings',
    input: {
      title: 'Generate Tweet Ideas',
      subtitle: 'Enter text or use voice input',
      modeText: 'Text',
      modeVoice: 'Voice',
      placeholder: 'Enter what you want to tweet about...',
    },
    button: {
      generate: 'Generate Tweet Ideas',
      generateFromAudio: 'Generate Tweet Ideas \u2192',
      reset: 'Reset',
      rerecord: 'Re-record',
      retry: 'Try again',
      newGenerate: 'Generate new',
      save: 'Save',
    },
    audio: {
      recorded: 'Recorded ({{size}} KB)',
    },
    tips: {
      title: 'Tips',
      tip1: 'Conclusion \u2192 Reason \u2192 Example — this structure scores high',
      tip2: 'Numbers and proper nouns boost specificity score',
      tip3: 'Record voice in a quiet environment for best results',
    },
    permissions: {
      title: 'Permissions Check',
      description: 'Check mic permissions and API key in settings. If errors persist, try reloading.',
    },
    status: {
      generating: 'Generating tweet candidates...',
      complete: 'Generation complete. Showing top candidates.',
      compressing: 'Compressing and transcribing audio...',
      generatingAfterTranscribe: 'Transcription complete \u2192 Generating tweets...',
      analyzing: 'Analyzing audio... Tweet generation will start automatically after transcription.',
      refining: 'Generating tweet ideas, scoring & diversity checking.',
    },
    progress: {
      transcribing: 'Transcribing audio...',
      generating: 'Generating & scoring...',
      complete: 'Complete',
      error: 'An error occurred',
      idle: 'Idle',
      label: 'Progress',
    },
    error: {
      apiKey: 'API Key is missing or invalid. Please re-configure in settings.',
      network: 'Network error. Check your connection and try again.',
      parse: 'Failed to parse generation results. Please wait and try again.',
      general: 'Processing failed',
      noAudio: 'No recording found. Please record again.',
      helpApiKey: 'Check API Key and provider settings',
      helpNetwork: 'Reconnect network and retry',
      helpAudio: 'Try re-recording and then run again',
    },
    transcription: {
      title: 'Transcription Result',
    },
    initial: {
      message: 'Enter text or record voice, then press "Generate Tweet Ideas".',
    },
    results: {
      title: 'Tweet Ideas Top',
    },
    breakdown: {
      freshness: 'Freshness',
      specificity: 'Specificity',
    },
    statusBadge: {
      complete: 'Complete',
      error: 'Needs attention',
    },
    length: 'Length:',
  },

  drafts: {
    title: 'Drafts',
    subtitle: 'Save and manage your generated tweets',
    exportImport: 'Export / Import',
    format: 'Format:',
    jsonImport: 'JSON Import',
    importSuccess: '{{count}} drafts imported',
    importError: 'Import failed: {{error}}',
    confirmDelete: 'Delete this draft?',
    search: 'Search drafts...',
    stats: {
      total: 'Total',
      favorites: 'Favorites',
      avgScore: 'Avg Score',
      tags: 'Tags',
    },
    filter: {
      all: 'All',
      favorites: 'Favorites',
    },
    sort: {
      newest: 'Newest first',
      oldest: 'Oldest first',
      scoreHigh: 'Highest score',
      scoreLow: 'Lowest score',
    },
    tags: {
      all: 'All tags',
      add: '+ Add tag',
    },
    empty: {
      search: 'No results found',
      default: 'No drafts yet',
    },
    notesPlaceholder: 'Notes (optional)',
    created: 'Created:',
  },

  scoreBreakdown: {
    totalScore: 'Overall Score',
    improvementHints: 'Improvement Hints',
    feedback: {
      excellent: 'Excellent! High potential to go viral',
      good: 'Good! Ready to post',
      needsWork: 'Could use a bit more polish',
    },
    freshness: {
      label: 'Freshness',
      description: 'Higher score means less similarity to past tweets',
      tips: { '0': 'Avoid repeating the same hooks', '1': 'Judged by n-gram similarity', '2': 'Penalty for familiar-sounding phrases' },
    },
    specificity: {
      label: 'Specificity',
      description: 'Higher score with more proper nouns, numbers, time expressions',
      tips: { '0': 'Include proper nouns (VSCode, Starbucks, etc.)', '1': 'Use numbers (3 times, 5 minutes, 90%, etc.)', '2': 'Use time expressions (today, yesterday, just now, etc.)' },
    },
    personaFit: {
      label: 'Persona Fit',
      description: 'How well it matches your writing style',
      tips: { '0': 'Appropriate emoji usage frequency', '1': 'Appropriate sentence length', '2': 'Consistent line break usage' },
    },
    replyHook: {
      label: 'Reply Hook',
      description: 'Elements that encourage replies',
      tips: { '0': 'Use question format', '1': 'Expressions that invite empathy', '2': 'Calls to action (tell me, try it, etc.)' },
    },
    readability: {
      label: 'Readability',
      description: 'Appropriate length, line breaks, character balance',
      tips: { '0': '100-200 chars is ideal', '1': '1-3 line breaks', '2': 'Keep sentences under 50 chars' },
    },
    risk: {
      label: 'Risk',
      description: 'Controversy risk (lower is better)',
      tips: { '0': 'Avoid extreme language', '1': 'Be careful with politics, religion, discrimination', '2': 'Watch out for misinformation & personal info' },
    },
  },

  weightAdjuster: {
    title: 'Scoring Weights',
    description: 'Adjust weights for score calculation',
    customWeights: 'Custom Weights',
    defaultWeights: 'Default Weights',
    resetButton: 'Reset',
    totalLabel: 'Total: {{total}} / 1.00',
    normalizationNote: '(will be normalized)',
    presetsSection: 'Presets',
    presets: {
      buzz: { title: 'Buzz Focus', description: 'Prioritize freshness & specificity' },
      style: { title: 'Style Focus', description: 'Prioritize persona fit' },
      engagement: { title: 'Engagement Focus', description: 'Prioritize reply hooks' },
      safety: { title: 'Safety Focus', description: 'Prioritize risk avoidance' },
    },
    metrics: {
      freshness: { label: 'Freshness', description: 'Higher score means less similarity to past tweets' },
      specificity: { label: 'Specificity', description: 'Higher score with more proper nouns, numbers, time expressions' },
      personaFit: { label: 'Persona Fit', description: 'How well it matches your writing style' },
      replyHook: { label: 'Reply Hook', description: 'Elements that encourage replies (questions, empathy, CTAs)' },
      readability: { label: 'Readability', description: 'Appropriate length, line breaks, character balance' },
      risk: { label: 'Risk', description: 'Controversy risk (lower is better, negative weight)' },
    },
  },

  voiceRecorder: {
    errors: {
      notSupported: 'Recording is not supported in your browser. Please try another browser.',
      recordingFailed: 'An error occurred during recording. Please try again.',
      startFailed: 'Could not start recording. Make sure no other app is using the microphone.',
      accessDenied: 'Microphone access denied. Please allow it in your browser permissions.',
      notFound: 'No microphone found. Please check your connection and settings.',
      notReadable: 'Microphone unavailable. It may be in use by another app.',
    },
    status: {
      requestingPermission: 'Requesting microphone permission...',
      recording: 'Recording',
      permissionDenied: 'Microphone permission denied',
      ready: 'Microphone ready',
      idle: 'Idle',
    },
    requesting: 'Requesting permission',
    info: {
      maxDuration: 'You can record up to {{maxDuration}} seconds. Recording is auto-saved.',
      remaining: '{{remaining}}s remaining \u00B7 Tap again to stop',
      recordingTap: 'Recording... Tap to stop',
      startTap: 'Tap to start recording',
    },
    permission: {
      required: 'Please grant permission',
    },
    aria: {
      stop: 'Stop recording',
      start: 'Start recording',
    },
  },

  errorBoundary: {
    clearDataConfirm: 'Clear all local data and reset?\n(Style & buzz analysis data will also be deleted)',
    title: 'An error occurred',
    message: 'Sorry, something went wrong with the app.',
    unexpectedError: 'An unexpected error occurred',
    developerInfo: 'Details (for developers)',
    retryButton: 'Retry',
    reloadButton: 'Reload page',
    clearDataButton: 'Clear data and reset',
    solutionTitle: 'How to fix:',
    solution1: '"Retry" may resolve temporary issues',
    solution2: '"Reload page" restarts the app',
    solution3: 'If problems persist, try "Clear data"',
  },

  archiveUpload: {
    title: 'Upload Twitter Archive',
    invalidFile: 'Invalid file format. Please upload tweets.js.',
    notEnoughTweets: 'Not enough tweets (minimum 10 required)',
    uploadFailed: 'Upload failed',
    dragDrop: 'Drag & drop <strong>tweets.js</strong>',
    orClickToSelect: 'or click to select a file',
    howToGet: 'How to get your Twitter Archive',
    step1: 'Request "Full tweet history" from',
    step2: 'Download available after 24 hours (email notification)',
    step3: 'Extract ZIP \u2192 Upload <code>data/tweets.js</code>',
    tryDemo: 'Try Demo (no API key needed)',
    skipWithApiKey: 'Skip (use Twitter API key)',
  },

  stats: {
    title: 'Statistics',
    notSet: 'Not set',
    totalGenerated: 'Total Tweets Generated',
    cumulative: 'Cumulative',
    avgScore: 'Average Score',
    qualityIndicator: 'Quality Indicator',
    bestScore: 'Best Score',
    best: 'Best',
    styleAnalysis: 'Style Analysis',
    lastUpdated: 'Last updated',
    buzzAnalysis: 'Buzz Analysis',
    setupStatus: 'Setup Status',
    setupComplete: 'Complete',
    setupIncomplete: 'Incomplete',
    readyMessage: 'Ready to go!',
    setupNeeded: 'Setup required',
    tip: 'Tip: Regularly updating your style & buzz analysis improves tweet quality.',
  },

  templateSelector: {
    selectingCount: 'Selecting types ({{count}}/{{max}})',
    selectOptional: 'Select type (optional)',
    selectTitle: 'Select tweet type ({{count}}/{{max}})',
    random: 'Random',
    limit: 'Limit',
    pattern: 'Pattern:',
    example: 'Example:',
  },

  tweetEditor: {
    title: 'Edit Tweet',
    emptyError: 'Tweet is empty',
    tooLongError: 'Exceeds 280 characters',
    placeholder: 'Edit tweet content...',
    tip: 'Tip: Line breaks, emojis, and numbers boost engagement',
    strategySettings: 'Tweet Strategy Settings',
  },

  apiKeyPrompt: {
    title: 'OpenAI API Key Required',
    description: 'Tweet generation and style analysis use OpenAI API (GPT-4o mini).\nPlease enter your own API key.',
    label: 'OpenAI API Key',
    getKeyInfo: 'Get your API key at OpenAI Platform',
    continueButton: 'Continue',
  },

  comingSoon: {
    title: 'Coming Soon',
    underDevelopment: 'This feature is under development',
  },

  protectedRoute: {
    checking: 'Checking authentication...',
  },
} as const;
